import { useState } from "react";
import { X, Loader2, Download, Copy, FileText, BookOpen } from "lucide-react";
import api from "../../lib/api";
import { toast } from "../ui/sonner";
import HidroMarkdown from "../HidroMarkdown";

const LENGTHS = [
  { key: "short", label: "Short", desc: "≤200 words · scannable bullets" },
  { key: "medium", label: "Medium", desc: "400–600 words · sections + prose" },
  { key: "long", label: "Long", desc: "1 000–1 500 words · full narrative" },
  { key: "exhaustive", label: "Exhaustive", desc: "2 500+ words · math, citations, edge cases" },
];

const AUDIENCES = [
  { key: "technician", label: "Technician", desc: "Action-first. Jargon ok. On-site steps." },
  { key: "engineer", label: "Engineer", desc: "Show the math. Cite standards." },
  { key: "customer", label: "Customer", desc: "Plain language. Outcome-focused. Friendly." },
  { key: "blog", label: "Blog post", desc: "SEO-aware. Fully anonymised." },
];

export default function CustomExportModal({ session, onClose }) {
  const [length, setLength] = useState("medium");
  const [audience, setAudience] = useState("technician");
  const [includeLog, setIncludeLog] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [result, setResult] = useState(null);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/engineering/sessions/${session.id}/export`,
        { length, audience, format: "markdown", include_chat_log: includeLog },
        { timeout: 180_000 },
      );
      setResult(data);
      // Wave 26.10.5 — every generated report is auto-published to the Library.
      if (data?.library_visible) {
        toast.success(
          data.category_key && data.category_key !== "general"
            ? `Saved to Library — ${data.category_key.replace(/_/g, " ")}`
            : "Saved to Library",
        );
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed.");
    } finally { setBusy(false); }
  };

  const copyOut = async () => {
    if (!result) return;
    const text = `# ${result.headline}\n\n${result.body}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied.");
    } catch {
      toast.error("Could not copy.");
    }
  };

  const download = () => {
    if (!result) return;
    const text = `# ${result.headline}\n\n${result.body}`;
    const blob = new Blob([text], { type: "text/markdown;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${(result.headline || "export").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-${audience}.md`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // Wave 26.7 — Server-rendered PDF (weasyprint). Light theme, A4, page
  // numbers, branded footer. Markdown is converted to real formatting.
  const downloadPdf = async () => {
    if (!result) return;
    setPdfBusy(true);
    try {
      const resp = await api.post(
        "/engineering/render-pdf",
        {
          headline: result.headline,
          body: result.body,
          audience: result.audience,
          length: result.length,
          generated_by: result.generated_by,
          generated_at: result.generated_at,
        },
        { responseType: "blob", timeout: 90_000 },
      );
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${(result.headline || "export").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-${audience}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("PDF downloaded.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF export failed.");
    } finally { setPdfBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-start sm:items-center justify-center p-4 pt-16 sm:pt-4" onClick={onClose} data-testid="eng-export-modal">
      <div className="hv-card w-full max-w-3xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-3 border-b border-white/10">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#1FB6C1]" />
            <div className="font-display font-bold text-sm">Custom export</div>
          </div>
          <button onClick={onClose} className="hv-btn hv-btn-ghost text-xs"><X className="w-4 h-4" /></button>
        </div>

        {!result ? (
          <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#a0aabf] mb-2">Length</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {LENGTHS.map((l) => (
                  <button
                    key={l.key}
                    onClick={() => setLength(l.key)}
                    className={`text-left p-2 rounded border transition ${length === l.key ? "border-[#1FB6C1] bg-[#1FB6C1]/[0.07]" : "border-white/10 hover:border-[#1FB6C1]/40"}`}
                    data-testid={`eng-export-len-${l.key}`}
                  >
                    <div className="font-bold text-xs">{l.label}</div>
                    <div className="text-[10px] text-[#a0aabf] mt-0.5">{l.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#a0aabf] mb-2">Audience</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {AUDIENCES.map((a) => (
                  <button
                    key={a.key}
                    onClick={() => setAudience(a.key)}
                    className={`text-left p-2 rounded border transition ${audience === a.key ? "border-[#1FB6C1] bg-[#1FB6C1]/[0.07]" : "border-white/10 hover:border-[#1FB6C1]/40"}`}
                    data-testid={`eng-export-aud-${a.key}`}
                  >
                    <div className="font-bold text-xs">{a.label}</div>
                    <div className="text-[10px] text-[#a0aabf] mt-0.5">{a.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            <label className="flex items-center gap-2 text-xs text-[#cbd2df] cursor-pointer">
              <input
                type="checkbox"
                checked={includeLog}
                onChange={(e) => setIncludeLog(e.target.checked)}
                className="h-3 w-3 accent-[#1FB6C1]"
                data-testid="eng-export-include-log"
              />
              Append the redacted Q&A transcript at the end
            </label>

            <div className="text-[11px] text-[#a0aabf] bg-white/[0.02] border border-white/10 rounded p-2.5">
              💡 The Library summary is fixed (medium · auto). Use this for tailored
              outputs — e.g. <em>exhaustive engineer write-up</em> for an internal
              report, <em>short customer-friendly</em> for a quote letter,
              or <em>blog</em> (anonymised) for marketing.
            </div>

            <div className="flex justify-end pt-1">
              <button onClick={generate} disabled={busy} className="hv-btn hv-btn-primary text-sm" data-testid="eng-export-generate">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Generate
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex-1 min-h-0 overflow-y-auto p-4">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#1FB6C1]">
                  {result.audience} · {result.length}
                </div>
                {result.library_visible && (
                  <div
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-emerald-300 border border-emerald-500/40 bg-emerald-500/10 rounded px-1.5 py-0.5"
                    data-testid="eng-export-library-badge"
                    title="This report has been auto-saved to the Library"
                  >
                    <BookOpen className="w-3 h-3" />
                    Saved to Library{result.category_key && result.category_key !== "general" ? ` · ${result.category_key.replace(/_/g, " ")}` : ""}
                  </div>
                )}
              </div>
              <h2 className="font-display text-xl font-bold mb-3">{result.headline}</h2>
              <HidroMarkdown text={result.body} className="text-sm" testId="eng-export-body" />
            </div>
            <div className="p-3 border-t border-white/10 flex items-center justify-between gap-2 shrink-0 flex-wrap">
              <button onClick={() => setResult(null)} className="hv-btn hv-btn-ghost text-xs" data-testid="eng-export-back">
                ← Generate another
              </button>
              <div className="flex gap-1 flex-wrap">
                <button onClick={copyOut} className="hv-btn hv-btn-ghost text-xs" data-testid="eng-export-copy">
                  <Copy className="w-3.5 h-3.5" /> Copy
                </button>
                <button onClick={download} className="hv-btn hv-btn-ghost text-xs" data-testid="eng-export-download-md">
                  <Download className="w-3.5 h-3.5" /> .md
                </button>
                <button
                  onClick={downloadPdf}
                  disabled={pdfBusy}
                  className="hv-btn hv-btn-primary text-xs"
                  data-testid="eng-export-download-pdf"
                  title="Download as styled PDF (A4, print-ready)"
                >
                  {pdfBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
                  PDF
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
